<template>
  <section class="w-full py-10 md:py-14">
    <div class="container mx-auto px-4 md:px-8">
      <div class="mb-6 flex items-end justify-between gap-3">
        <h2 class="text-2xl md:text-3xl font-bold">{{ title }}</h2>
        <NuxtLink
          to="/catalog"
          class="text-sm underline hover:opacity-80 shrink-0"
        >
          Дивитись всі
        </NuxtLink>
      </div>

      <ClientOnly>
        <template #default>
          <CatalogProductGrid
            v-if="loading || items.length"
            :items="items"
            :loading="loading"
            :skeleton-count="limit"
            density="roomy"
          />
          <div v-else class="text-center text-gray-500 py-12">
            Ви ще нічого не переглядали.
          </div>
        </template>

        <!-- Fallback під час гідрації -->
        <template #fallback>
          <CatalogProductGrid loading :skeleton-count="limit" density="roomy" />
        </template>
      </ClientOnly>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { useNuxtApp } from "#app";
import {
  collection,
  getDocs,
  query,
  where,
  documentId,
} from "firebase/firestore";
import { getRecentlyViewedPairs } from "~/utils/recentlyViewed";

const props = defineProps({
  title: { type: String, default: "Ви переглядали" },
  limit: { type: Number, default: 8 },
});

const { $db } = useNuxtApp();
const items = ref([]); // [{ key, product, variant }]
const loading = ref(true); // ✅ одразу true, щоб не було «порожньої» секції

// helpers
const chunk = (arr, n) =>
  Array.from({ length: Math.ceil(arr.length / n) }, (_, i) =>
    arr.slice(i * n, i * n + n),
  );

async function fetchProductsMap(ids) {
  const uniq = Array.from(new Set(ids)).filter(Boolean);
  if (!uniq.length) return new Map();
  const batches = chunk(uniq, 10);
  const map = new Map();
  for (const b of batches) {
    const snap = await getDocs(
      query(collection($db, "products"), where(documentId(), "in", b)),
    );
    snap.docs.forEach((d) => map.set(d.id, { id: d.id, ...d.data() }));
  }
  return map;
}

async function fetchVariants(pid) {
  const snap = await getDocs(collection($db, "products", pid, "variants"));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

async function runLimited(tasks, limit = 3) {
  let i = 0;
  await Promise.all(
    Array.from({ length: Math.min(limit, tasks.length) }, async () => {
      while (i < tasks.length) {
        const fn = tasks[i++];

        await fn();
      }
    }),
  );
}

let disposed = false;
onUnmounted(() => {
  disposed = true;
});

async function load() {
  try {
    loading.value = true; // ✅ виставляємо на початку
    // беремо з запасом, але покажемо не більше props.limit
    const pairs = getRecentlyViewedPairs(props.limit * 3);
    if (!pairs.length) {
      items.value = [];
      return;
    }

    const pidMap = await fetchProductsMap(pairs.map((p) => p.productId));
    if (disposed) return;

    // Кеш варіантів з лімітом конкурентності
    const vCache = new Map();
    const pids = Array.from(
      new Set(pairs.map((p) => p.productId).filter(Boolean)),
    );
    const tasks = pids.map((pid) => async () => {
      const prod = pidMap.get(pid);
      if (!prod || prod.published === false) {
        vCache.set(pid, []);
        return;
      }
      try {
        const list = await fetchVariants(pid);
        vCache.set(pid, list);
      } catch {
        vCache.set(pid, []);
      }
    });
    await runLimited(tasks, 3);
    if (disposed) return;

    const result = [];
    for (const pr of pairs) {
      const p = pidMap.get(pr.productId);
      if (!p || p.published === false) continue;

      const list = vCache.get(pr.productId) || [];
      const color = String(pr.color || "");
      const size = String(pr.size || "");

      const match =
        list.find(
          (v) =>
            String(v?.color?.value || v?.color || "") === color &&
            String(v?.size || "") === size,
        ) ||
        list.find((v) => String(v?.color?.value || v?.color || "") === color) ||
        list.find((v) => String(v?.size || "") === size) ||
        null;

      result.push({
        key: `${p.id}__${match?.id || `${color}|${size}`}`,
        product: p,
        variant: match,
      });
      if (result.length >= props.limit) break;
    }
    if (!disposed) items.value = result;
  } finally {
    if (!disposed) loading.value = false; // ✅ завжди знімаємо лоадінг
  }
}

onMounted(load);
</script>
